"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EVIDENCE_CONTEXT = exports.MUST_HAVE_MIN_MATCH = exports.MUST_HAVE_CAP = void 0;
exports.breakdownRows = breakdownRows;
exports.criterionTagLine = criterionTagLine;
exports.computeMatchScore = computeMatchScore;
exports.scoreMatches = scoreMatches;
exports.criterionMatch = criterionMatch;
exports.normaliseTerm = normaliseTerm;
exports.factsFromFormAnswers = factsFromFormAnswers;
exports.atsRank = atsRank;
exports.starsOf = starsOf;
exports.focusScore = focusScore;
exports.compareFocused = compareFocused;
exports.degreeOf = degreeOf;
exports.degreeLevelOf = degreeLevelOf;
exports.factsFromImport = factsFromImport;
exports.factsFromApplication = factsFromApplication;
const enums_1 = require("./enums");
const imports_1 = require("./imports");
const job_builder_1 = require("./job-builder");
const enums_2 = require("./enums");
const jobs_1 = require("./jobs");
// S14 — F7.04, the §L1 auto-match score. Pure, so the formula is unit-tested exactly
// (CLAUDE.md §12) and the API only has to feed it and store what it returns: a score
// is computed when the application arrives and when the job's criteria change, and
// never at read time.
//
// Two decisions shape the inputs (DECISIONS-LOG S14 Q1 and the S14 plan):
// - Skills and certifications are typed by the candidate, so "tagged" means the
//   typed term equals the criterion once case, spaces and punctuation are ignored.
// - §L1's "appears in resume text but not tagged = 0.4" needs a CV's text. R5's
//   `atsRank` (§L30, at the end of this file) reads it; `computeMatchScore` doesn't.
/** §L1: a missing must-have caps the score here — it never rejects (that is §L8's job). */
exports.MUST_HAVE_CAP = 49;
/** §L1: "every must_have criterion has match ≥ 0.5". */
exports.MUST_HAVE_MIN_MATCH = 0.5;
const SKILL_TAGGED = 1;
const SKILL_SYNONYM = 0.7;
const EXPERIENCE_RAMP_YEARS = 2;
const EDUCATION_ONE_BELOW = 0.4;
const LANGUAGE_ONE_BELOW = 0.5;
const LOCATION_RELOCATE = 0.6;
const MONTHS_PER_YEAR = 12;
const MAX_SCORE = 100;
/** Names each breakdown row from the §L30 matches stored beside it, by criterion key. */
function breakdownRows(breakdown, matches) {
    const byKey = new Map((matches ?? []).map((match) => [match.key, match]));
    return breakdown.map((item) => {
        const match = byKey.get(item.criterionId);
        return { ...item, importance: match?.importance ?? null, kind: match?.kind ?? null };
    });
}
/** "Yes/No · Important" — or, for a score from before R5, §L1's "Skill · Must have". */
function criterionTagLine(row) {
    if (row.importance !== null && row.kind !== null)
        return `${job_builder_1.CRITERION_KIND_LABELS[row.kind]} · ${job_builder_1.CRITERION_IMPORTANCE_LABELS[row.importance]}`;
    return `${jobs_1.CRITERION_TYPE_LABELS[row.type]} · ${jobs_1.CRITERION_CLASS_LABELS[row.class]}`;
}
/** F7.04: every criterion matched, then the formula. */
function computeMatchScore(job, facts, penalty) {
    const matches = job.criteria.map((criterion) => ({
        criterion,
        ...criterionMatch(criterion, facts, job.workMode),
    }));
    return scoreMatches(matches, penalty);
}
/**
 * §L1's formula, given each criterion's match:
 *
 *   raw   = Σ weight × match
 *   final = raw                  when every must-have is ≥ 0.5
 *         = min(raw, 49)         otherwise
 *
 * A §L8 penalty "subtracts n points from the L1 score" — the final score, so it
 * applies after the cap (S14 plan); taken before, a capped candidate's penalty would
 * vanish. Floored at 0.
 */
function scoreMatches(matches, penalty) {
    const breakdown = matches.map(({ criterion, match, evidence }) => ({
        criterionId: criterion.id,
        label: criterion.label,
        type: criterion.type,
        class: criterion.class,
        weight: criterion.weight,
        match,
        points: roundTo2(criterion.weight * match),
        evidence,
    }));
    const rawScore = roundTo2(breakdown.reduce((sum, item) => sum + criterionPoints(item), 0));
    const missingMustHaves = breakdown
        .filter((item) => item.class === 'must_have' && item.match < exports.MUST_HAVE_MIN_MATCH)
        .map((item) => item.label);
    const isMissingMustHave = missingMustHaves.length > 0;
    const capped = isMissingMustHave ? Math.min(rawScore, exports.MUST_HAVE_CAP) : rawScore;
    const score = clamp(Math.round(capped) - penalty, 0, MAX_SCORE);
    return { score, rawScore, isMissingMustHave, missingMustHaves, penalty, breakdown };
}
/** Unrounded, so the raw score sums exactly rather than summing rounded parts. */
function criterionPoints(item) {
    return item.weight * item.match;
}
// --- Per-criterion match, §L1's table ------------------------------------------------
function criterionMatch(criterion, facts, workMode) {
    switch (criterion.type) {
        case 'skill':
            return skillMatch(criterion, facts.skills);
        case 'experience_years':
            return experienceMatch(criterion, facts.totalExperienceMonths);
        case 'education_level':
            return educationMatch(criterion, facts.highestEducation);
        case 'certification':
            return certificationMatch(criterion, facts.certifications);
        case 'language':
            return languageMatch(criterion, facts.languages);
        case 'location':
            return locationMatch(criterion, facts, workMode);
    }
}
/** Exact tag 1.0 · listed synonym 0.7 · absent 0.0 (the 0.4 resume-text tier needs S33). */
function skillMatch(criterion, skills) {
    const typed = new Map(skills.map((skill) => [normaliseTerm(skill), skill]));
    const exact = typed.get(normaliseTerm(criterion.label));
    if (exact !== undefined)
        return { match: SKILL_TAGGED, evidence: `Listed "${exact}"` };
    for (const synonym of stringListParam(criterion.params, 'synonyms')) {
        const found = typed.get(normaliseTerm(synonym));
        if (found !== undefined)
            return { match: SKILL_SYNONYM, evidence: `Listed "${found}", a synonym` };
    }
    return { match: 0, evidence: 'Not listed' };
}
/** 0 below (min − 2); linear up to min; 1.0 at or above min — never more. */
function experienceMatch(criterion, months) {
    return yearsMatch(numberParam(criterion.params, 'min_years') ?? 0, months);
}
function yearsMatch(minYears, months) {
    const needs = `needs ${formatYears(minYears)}`;
    if (months === null)
        return { match: 0, evidence: `Not given; ${needs}` };
    const years = months / MONTHS_PER_YEAR;
    const evidence = `${formatYears(years)}; ${needs}`;
    if (years >= minYears)
        return { match: 1, evidence };
    const floor = minYears - EXPERIENCE_RAMP_YEARS;
    if (years <= floor)
        return { match: 0, evidence };
    return { match: roundTo2((years - floor) / EXPERIENCE_RAMP_YEARS), evidence };
}
/** Meets or exceeds 1.0 · one level below 0.4 · more than one below 0.0. */
function educationMatch(criterion, level) {
    const required = educationParam(criterion.params, 'min_level');
    if (!required)
        return { match: 0, evidence: 'No level set on the criterion' };
    const needs = `needs ${enums_2.EDUCATION_LEVEL_LABELS[required]}`;
    if (!level)
        return { match: 0, evidence: `Not given; ${needs}` };
    const gap = enums_1.EDUCATION_LEVELS.indexOf(required) - enums_1.EDUCATION_LEVELS.indexOf(level);
    const evidence = `${enums_2.EDUCATION_LEVEL_LABELS[level]}; ${needs}`;
    if (gap <= 0)
        return { match: 1, evidence };
    return { match: gap === 1 ? EDUCATION_ONE_BELOW : 0, evidence };
}
/** Present 1.0 · absent 0.0. */
function certificationMatch(criterion, certifications) {
    const wanted = normaliseTerm(criterion.label);
    const found = certifications.find((name) => normaliseTerm(name) === wanted);
    return found === undefined ? { match: 0, evidence: 'Not listed' } : { match: 1, evidence: `Listed "${found}"` };
}
/** At or above the required proficiency 1.0 · one level below 0.5 · else 0.0. */
function languageMatch(criterion, languages) {
    const language = stringParam(criterion.params, 'language') ?? criterion.label;
    const required = proficiencyParam(criterion.params, 'min_proficiency');
    if (!required)
        return { match: 0, evidence: 'No proficiency set on the criterion' };
    const needs = `needs ${jobs_1.LANGUAGE_PROFICIENCY_LABELS[required]}`;
    const declared = languages.find((row) => normaliseTerm(row.language) === normaliseTerm(language));
    if (!declared)
        return { match: 0, evidence: `${language} not listed; ${needs}` };
    const gap = enums_1.LANGUAGE_PROFICIENCIES.indexOf(required) - enums_1.LANGUAGE_PROFICIENCIES.indexOf(declared.proficiency);
    const evidence = `${jobs_1.LANGUAGE_PROFICIENCY_LABELS[declared.proficiency]}; ${needs}`;
    if (gap <= 0)
        return { match: 1, evidence };
    return { match: gap === 1 ? LANGUAGE_ONE_BELOW : 0, evidence };
}
/** Same city 1.0 · elsewhere but willing to relocate 0.6 · elsewhere, not willing 0.0 · remote role 1.0. */
function locationMatch(criterion, facts, workMode) {
    if (workMode === 'remote')
        return { match: 1, evidence: 'Remote role' };
    const city = stringParam(criterion.params, 'city') ?? criterion.label;
    if (facts.city !== null && normaliseTerm(facts.city) === normaliseTerm(city)) {
        return { match: 1, evidence: `Lives in ${facts.city}` };
    }
    const where = facts.city === null ? 'City not given' : `Lives in ${facts.city}`;
    if (facts.isWillingToRelocate === true)
        return { match: LOCATION_RELOCATE, evidence: `${where}; willing to relocate` };
    return { match: 0, evidence: `${where}; not willing to relocate` };
}
// --- Helpers ---------------------------------------------------------------------------
/**
 * Case, spaces and punctuation ignored (DECISIONS-LOG S14 Q1): "MS-Excel" and
 * "ms excel" are one term. Spelling is not forgiven — that is what synonyms are for.
 */
function normaliseTerm(text) {
    return text.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}
function stringParam(params, key) {
    const value = params[key];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}
function numberParam(params, key) {
    const value = params[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function stringListParam(params, key) {
    const value = params[key];
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}
function educationParam(params, key) {
    const value = params[key];
    return enums_1.EDUCATION_LEVELS.find((level) => level === value) ?? null;
}
function proficiencyParam(params, key) {
    const value = params[key];
    return enums_1.LANGUAGE_PROFICIENCIES.find((level) => level === value) ?? null;
}
function formatYears(years) {
    const rounded = roundTo1(years);
    return `${rounded} ${rounded === 1 ? 'year' : 'years'}`;
}
function roundTo2(value) {
    return Math.round(value * 100) / 100;
}
function roundTo1(value) {
    return Math.round(value * 10) / 10;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
// --- Reading a stored application back -----------------------------------------------
/**
 * The facts §L1 reads, from `applications.form_answers` — what this application said,
 * never the candidate's later profile, so a score cannot drift when the same person
 * applies elsewhere (S14 plan). Stored JSON is untrusted: anything unreadable counts
 * as not given.
 */
function factsFromFormAnswers(stored) {
    const record = isRecord(stored) ? stored : {};
    const months = record.totalExperienceMonths;
    const education = record.highestEducation;
    const relocate = record.isWillingToRelocate;
    const city = record.city;
    return {
        skills: stringList(record.skills),
        totalExperienceMonths: typeof months === 'number' && Number.isFinite(months) ? months : null,
        highestEducation: enums_1.EDUCATION_LEVELS.find((level) => level === education) ?? null,
        certifications: stringList(record.certifications),
        languages: Array.isArray(record.languages) ? record.languages.flatMap(languageRow) : [],
        city: typeof city === 'string' && city.trim() !== '' ? city : null,
        isWillingToRelocate: typeof relocate === 'boolean' ? relocate : null,
    };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function stringList(value) {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}
function languageRow(value) {
    if (!isRecord(value) || typeof value.language !== 'string')
        return [];
    const proficiency = enums_1.LANGUAGE_PROFICIENCIES.find((level) => level === value.proficiency);
    return proficiency ? [{ language: value.language, proficiency }] : [];
}
// --- R5: the §L30 ATS ranking ----------------------------------------------------------
//
// §L30 extends §L1: each criterion is judged by its kind from its column (or, for an
// application that came another way, its form answers) and the CV's text, and then
// §L1's formula, weights and cap run unchanged (`scoreMatches`). Pure, so §L30's worked
// example is a unit test before any screen (CLAUDE.md §12).
/** §L30: a Look-for word in a column, or Mentioned anywhere. */
const FULL_MATCH = 1;
/** §L30's synonym: a "Partly counts" word (DECISIONS-LOG "Start R5" Q2). */
const PARTLY_WORD_MATCH = 0.7;
/** §L30: found only in the CV's text — §L1's "in the resume but not tagged". */
const CV_ONLY_MATCH = 0.4;
/** §L30 Level: exactly one below the minimum. */
const LEVEL_ONE_BELOW = 0.5;
const DEFAULT_MIN_LEVEL = 3;
/** §L30 "Evidence": characters kept either side of the match, for RNK-02's highlight. */
exports.EVIDENCE_CONTEXT = 60;
/** §L30 stars: 80–100 ★5 · 60–79 ★4 · 40–59 ★3 · 20–39 ★2 · 0–19 ★1. */
const STAR_FLOORS = [
    [80, 5],
    [60, 4],
    [40, 3],
    [20, 2],
];
const LOWEST_STARS = 1;
const TOP_FIT_STARS = 4;
const MAYBE_STARS = 3;
const PERCENT = 100;
/** A whole answer that means yes or no, after folding ("N/A" → "n a"). */
const YES_ANSWERS = new Set(['yes', 'y', 'true', 'ok']);
const NO_ANSWERS = new Set(['no', 'n', 'false', 'none', 'nil', 'n a', 'na']);
/** F23.02: every criterion judged, corrections applied, then §L1's formula and §L30's stars and tab. */
function atsRank(criteria, facts, corrections, penalty) {
    const cv = facts.cv.status === 'ready' && facts.cv.text.trim() !== '' ? searchable(facts.cv.text) : null;
    const matches = criteria.map((criterion) => rankedCriterion(criterion, judge(criterion, facts.columns[criterion.key] ?? null, facts.cv, cv), corrections[criterion.key] ?? null));
    const result = scoreMatches(criteria.map((criterion, index) => ({
        criterion: scoringCriterionOf(criterion),
        match: matches[index]?.match ?? 0,
        evidence: matches[index]?.reason ?? '',
    })), penalty);
    const stars = starsOf(result.score);
    const isMustHaveMissing = matches.some((match) => match.importance === 'must_have' && match.mark === 'missing');
    return {
        ...result,
        stars,
        tab: isMustHaveMissing ? 'needs_a_look' : tabOfStars(stars),
        coverage: coverageOf(matches),
        matches,
        isProvisional: facts.cv.status === 'coming',
    };
}
/** §L30 stars from the final score. */
function starsOf(score) {
    return STAR_FLOORS.find(([floor]) => score >= floor)?.[1] ?? LOWEST_STARS;
}
/** §L30 tabs (O28) for a candidate with no Missing must-have. */
function tabOfStars(stars) {
    if (stars >= TOP_FIT_STARS)
        return 'top_fit';
    return stars === MAYBE_STARS ? 'maybe' : 'low_fit';
}
function coverageOf(matches) {
    const total = matches.reduce((sum, match) => sum + match.weight, 0);
    if (total === 0)
        return 0;
    const read = matches.filter((match) => match.mark !== 'missing').reduce((sum, match) => sum + match.weight, 0);
    return Math.round((PERCENT * read) / total);
}
/**
 * F23.04 focus: `round(100 × Σ w·m / Σ w)` over the chosen criteria, uncapped (§L30). Summed
 * in hundredths of a point so 92.5 is 92.5 and rounds to 93, not 92.4999… to 92. Null when
 * the chosen criteria weigh nothing.
 */
function focusScore(matches, keys) {
    let hundredths = 0;
    let weights = 0;
    for (const match of matches) {
        if (!keys.has(match.key))
            continue;
        hundredths += Math.round(match.weight * match.match * PERCENT);
        weights += match.weight;
    }
    return weights === 0 ? null : Math.round(hundredths / weights);
}
/** §L30 focus order: focus, then the full score, then import order. */
function compareFocused(a, b) {
    return (b.focus ?? -1) - (a.focus ?? -1) || b.score - a.score || a.order - b.order;
}
function scoringCriterionOf(criterion) {
    return {
        id: criterion.key,
        type: criterion.type,
        label: criterion.label,
        class: (0, job_builder_1.classFromImportance)(criterion.importance),
        weight: criterion.weight,
        params: criterion.params,
    };
}
function rankedCriterion(criterion, machine, correction) {
    const machineMark = markOf(machine);
    const match = correction ? (correction.mark === 'met' ? FULL_MATCH : 0) : machine.match;
    return {
        key: criterion.key,
        label: criterion.label,
        kind: criterion.kind,
        importance: criterion.importance,
        weight: criterion.weight,
        mark: correction ? correction.mark : machineMark,
        match,
        points: roundTo2(criterion.weight * match),
        source: machine.source,
        reason: machine.reason,
        evidence: machine.evidence,
        machineMark,
        machineMatch: machine.match,
        correction,
    };
}
function markOf(judgement) {
    if (!judgement.isRead)
        return 'missing';
    if (judgement.match >= FULL_MATCH)
        return 'met';
    return judgement.match > 0 ? 'partly' : 'not_met';
}
/**
 * The column first, then the CV's text (§L30, PRD AT-1). Years never reads the CV. A
 * criterion is Missing only when its column said nothing **and** the CV couldn't be read.
 */
function judge(criterion, reading, cvText, cv) {
    const column = judgeColumn(criterion, reading);
    if (criterion.kind === 'years')
        return column ?? missing("No years given, and the CV isn't read for years");
    if (column && column.match >= FULL_MATCH)
        return column;
    const fromCv = cv ? judgeCv(criterion, cv) : null;
    if (column && fromCv && column.match === 0 && fromCv.match === 0)
        return { ...column, reason: 'Not in the answer or the CV' };
    if (column && fromCv)
        return fromCv.match > column.match ? fromCv : column;
    return column ?? fromCv ?? missing(missingReason(cvText));
}
function missing(reason) {
    return { match: 0, source: 'none', reason, evidence: null, isRead: false };
}
function missingReason(cv) {
    switch (cv.status) {
        case 'coming':
            return 'Nothing in the answers yet, and the CV is still coming';
        case 'unreadable':
            return "Nothing in the answers, and the CV is a scanned image, so its text couldn't be read";
        case 'none':
        case 'ready':
            return 'Nothing in the answers, and no CV to read';
    }
}
function read(match, source, reason, evidence = null) {
    return { match, source, reason, evidence, isRead: true };
}
/** A column's judgement, or null when it said nothing (no column, empty, or no number where one was needed). */
function judgeColumn(criterion, reading) {
    if (reading === null)
        return null;
    if ('match' in reading)
        return read(reading.match, 'column', reading.reason);
    if (reading.text.trim() === '')
        return null;
    switch (criterion.kind) {
        case 'yes_no':
            return judgeYesNoColumn(criterion, searchable(reading.text));
        case 'level':
            return judgeLevelColumn(criterion, reading.text);
        case 'years':
            return judgeYearsColumn(criterion, reading.text);
        case 'keywords':
        case 'mentioned':
            return judgeWordsColumn(criterion, searchable(reading.text));
    }
}
/** §L30 Yes/No: a Look-for word or "yes" 1.0 · degree met 1.0 · a Partly word 0.7 · one degree below 0.4 · else 0. */
function judgeYesNoColumn(criterion, column) {
    const word = firstOf(column, fullWords(criterion));
    if (word)
        return read(FULL_MATCH, 'column', `"${word.word}" in the answer`, word.evidence);
    if (YES_ANSWERS.has(column.text))
        return read(FULL_MATCH, 'column', 'Answered yes', wholeOf(column));
    const degree = degreeOf(criterion);
    const level = degree ? (0, imports_1.educationLevelOf)(column.raw) : null;
    const gap = degree && level ? enums_1.EDUCATION_LEVELS.indexOf(degree) - enums_1.EDUCATION_LEVELS.indexOf(level) : null;
    if (degree && level && gap !== null && gap <= 0) {
        return read(FULL_MATCH, 'column', `${enums_2.EDUCATION_LEVEL_LABELS[level]}; needs ${enums_2.EDUCATION_LEVEL_LABELS[degree]}`, wholeOf(column));
    }
    const partly = firstOf(column, criterion.partlyCounts);
    if (partly)
        return read(PARTLY_WORD_MATCH, 'column', `"${partly.word}" in the answer, which partly counts`, partly.evidence);
    if (degree && level && gap === 1) {
        return read(EDUCATION_ONE_BELOW, 'column', `${enums_2.EDUCATION_LEVEL_LABELS[level]}, one level below ${enums_2.EDUCATION_LEVEL_LABELS[degree]}`, wholeOf(column));
    }
    if (NO_ANSWERS.has(column.text))
        return read(0, 'column', 'Answered no', quoteOf(column));
    return read(0, 'column', "The answer doesn't say so", quoteOf(column));
}
/** §L30 Level: n ≥ min 1.0 · n = min − 1 0.5 · lower 0. A cell with no number says nothing. */
function judgeLevelColumn(criterion, text) {
    const number = /\d+(?:\.\d+)?/.exec(text);
    if (!number)
        return null;
    const level = Number(number[0]);
    const min = criterion.minLevel ?? DEFAULT_MIN_LEVEL;
    const reason = `${level}; needs ${min}`;
    if (level >= min)
        return read(FULL_MATCH, 'column', reason);
    return read(level >= min - 1 ? LEVEL_ONE_BELOW : 0, 'column', reason);
}
/** §L30 Years: §L1's ramp. A cell with no number of years says nothing. */
function judgeYearsColumn(criterion, text) {
    const months = (0, imports_1.experienceMonthsOf)(text);
    if (months === null)
        return null;
    const { match, evidence } = yearsMatch(criterion.minLevel ?? 0, months);
    return read(match, 'column', evidence);
}
/** §L30 Keywords and Mentioned, in a column: a Look-for word 1.0 · a Partly word 0.7 · else 0. */
function judgeWordsColumn(criterion, column) {
    const word = firstOf(column, fullWords(criterion));
    if (word)
        return read(FULL_MATCH, 'column', `"${word.word}" in the answer`, word.evidence);
    const partly = firstOf(column, criterion.partlyCounts);
    if (partly)
        return read(PARTLY_WORD_MATCH, 'column', `"${partly.word}" in the answer, which partly counts`, partly.evidence);
    return read(0, 'column', 'Not in the answer', quoteOf(column));
}
/**
 * The CV's text. Mentioned reads it at full strength (a Look-for word 1.0, a Partly word
 * 0.7); every other kind counts any of its words at 0.4, "only in the CV text" (§L30).
 */
function judgeCv(criterion, cv) {
    if (criterion.kind === 'mentioned') {
        const word = firstOf(cv, fullWords(criterion));
        if (word)
            return read(FULL_MATCH, 'cv', `"${word.word}" in the CV`, word.evidence);
        const partly = firstOf(cv, criterion.partlyCounts);
        if (partly)
            return read(PARTLY_WORD_MATCH, 'cv', `"${partly.word}" in the CV, which partly counts`, partly.evidence);
        return read(0, 'cv', 'Not in the answers or the CV');
    }
    const word = firstOf(cv, [...fullWords(criterion), ...criterion.partlyCounts]);
    if (!word)
        return read(0, 'cv', 'Not in the answers or the CV');
    const reason = criterion.kind === 'level' ? `"${word.word}" in the CV; level unknown` : `"${word.word}" only in the CV`;
    return read(CV_ONLY_MATCH, 'cv', reason, word.evidence);
}
function fullWords(criterion) {
    return criterion.lookFor.length > 0 ? criterion.lookFor : [criterion.label];
}
/**
 * A Yes/No criterion's degree on §L30's ladder: an old "education level" keeps its stored
 * minimum; a builder criterion named like a degree ("Master's degree") is read from its name.
 */
function degreeOf(criterion) {
    if (criterion.kind !== 'yes_no')
        return null;
    if (criterion.type === 'education_level')
        return educationParam(criterion.params, 'min_level');
    return (0, imports_1.educationLevelOf)(criterion.label);
}
const DEGREE_WORD = /\bdegree\b/i;
/**
 * `degreeOf` reads only Yes/No criteria, because ranking scores a library "Bachelor's degree" as a Mentioned word list
 * (kind "mentioned", type "skill"). Where the question is "does this job ask for a degree", a Keywords or Mentioned line named
 * "... degree" counts too. Requiring the word "degree" keeps a skill such as "BA" (business analysis) from being read as a Bachelor's.
 * The ideal candidate interview and the degree fact's CV fallback (ADR 0061) both use it.
 */
function degreeLevelOf(criterion) {
    const degree = degreeOf(criterion);
    if (degree)
        return degree;
    if (criterion.kind !== 'keywords' && criterion.kind !== 'mentioned')
        return null;
    return DEGREE_WORD.test(criterion.label) ? (0, imports_1.educationLevelOf)(criterion.label) : null;
}
const COMBINING_MARKS = /[̀-ͯ]/g;
const LETTER_OR_NUMBER = /^[\p{L}\p{N}]+$/u;
const LETTERS = /^\p{L}+$/u;
/** "Master's" is "masters". */
const DROPPED_MARKS = new Set(["'", '’', '‘', 'ʼ']);
const ABBREVIATION_MAX_LETTERS = 2;
/**
 * Case and accents folded, every run of punctuation and space one break, except that an
 * abbreviation's dot goes ("M.Sc." → "msc", §L30) while a word's stays a break
 * ("github.com/rahim" → "github com rahim"): R4's `normaliseHeader` rule, keeping a map back
 * to the original so evidence can be cut from it.
 */
function searchable(raw) {
    let text = '';
    const at = [];
    let isBreakPending = false;
    let run = 0;
    let isLetterRun = true;
    for (let index = 0; index < raw.length;) {
        const char = String.fromCodePoint(raw.codePointAt(index) ?? 0);
        const folded = char.normalize('NFKD').replace(COMBINING_MARKS, '').toLowerCase();
        if (folded === '' || DROPPED_MARKS.has(char)) {
            // A lone accent, or an apostrophe: no character and no break.
        }
        else if (LETTER_OR_NUMBER.test(folded)) {
            if (isBreakPending && text !== '') {
                text += ' ';
                at.push(index);
            }
            isBreakPending = false;
            for (const piece of folded) {
                text += piece;
                at.push(index);
            }
            run += 1;
            isLetterRun = isLetterRun && LETTERS.test(folded);
        }
        else if (char === '.' && !isBreakPending && isLetterRun && run >= 1 && run <= ABBREVIATION_MAX_LETTERS) {
            run = 0;
        }
        else {
            isBreakPending = true;
            run = 0;
            isLetterRun = true;
        }
        index += char.length;
    }
    return { raw, text, at };
}
/** The earliest of the words found in the text, on whole words. */
function firstOf(source, words) {
    const padded = ` ${source.text} `;
    let best = null;
    for (const word of words) {
        const wanted = searchable(word).text;
        if (wanted === '')
            continue;
        const position = padded.indexOf(` ${wanted} `);
        if (position >= 0 && (!best || position < best.position))
            best = { word, position, length: wanted.length };
    }
    if (!best)
        return null;
    const start = source.at[best.position] ?? 0;
    const last = source.at[best.position + best.length - 1] ?? start;
    return { word: best.word, evidence: evidenceOf(source.raw, start, last + codePointWidth(source.raw, last)) };
}
/** The whole answer, all of it the match ("B.Sc." for a degree one level below). */
function wholeOf(source) {
    return evidenceOf(source.raw, 0, source.raw.length);
}
/** The answer quoted with nothing marked: it was read, and it said no. */
function quoteOf(source) {
    return { text: source.raw.slice(0, notInsidePair(source.raw, Math.min(source.raw.length, 2 * exports.EVIDENCE_CONTEXT))).replace(/\s/g, ' '), start: 0, end: 0 };
}
/** §L30: the match and up to 60 characters either side, white space flattened, never splitting a character. */
function evidenceOf(raw, start, end) {
    const from = notInsidePair(raw, Math.max(0, start - exports.EVIDENCE_CONTEXT));
    const to = notInsidePair(raw, Math.min(raw.length, end + exports.EVIDENCE_CONTEXT));
    return { text: raw.slice(from, to).replace(/\s/g, ' '), start: start - from, end: end - from };
}
const ASTRAL_START = 0xffff;
const LOW_SURROGATE_FIRST = 0xdc00;
const LOW_SURROGATE_LAST = 0xdfff;
function codePointWidth(raw, index) {
    return (raw.codePointAt(index) ?? 0) > ASTRAL_START ? 2 : 1;
}
/** Postgres refuses half a surrogate pair in JSON, so a cut never lands between the two. */
function notInsidePair(raw, index) {
    const code = raw.charCodeAt(index);
    return index > 0 && index < raw.length && code >= LOW_SURROGATE_FIRST && code <= LOW_SURROGATE_LAST ? index - 1 : index;
}
// --- A candidate's facts -----------------------------------------------------------------
/**
 * F23.02 for an imported row: each criterion's column, as matched at import (R4's coverage
 * check, keyed by criterion key), read from the row's cells. A criterion added after the
 * import has no column, so only the CV can answer it.
 */
function factsFromImport(criterionColumns, cells, cv) {
    const columns = {};
    for (const [key, header] of Object.entries(criterionColumns)) {
        columns[key] = header === null ? null : { text: cells[header] ?? '' };
    }
    return { columns, cv };
}
/**
 * F23.02 for an application that came any other way (career site, typed in by hand): its
 * form answers stand in for columns (DECISIONS-LOG "Start R5" Q4), read by the same kinds.
 * Language and location have no §L30 rule, so §L1 judges them.
 */
function factsFromApplication(criteria, facts, workMode, cv) {
    const columns = {};
    for (const criterion of criteria)
        columns[criterion.key] = formReading(criterion, facts, workMode);
    return { columns, cv };
}
function formReading(criterion, facts, workMode) {
    if (criterion.kind === 'years') {
        return facts.totalExperienceMonths === null ? null : { text: `${facts.totalExperienceMonths} months` };
    }
    if (degreeOf(criterion)) {
        return facts.highestEducation === null ? null : { text: enums_2.EDUCATION_LEVEL_LABELS[facts.highestEducation] };
    }
    if (criterion.type === 'language' || criterion.type === 'location') {
        const hasAnswer = criterion.type === 'language' ? facts.languages.length > 0 : facts.city !== null || workMode === 'remote';
        if (!hasAnswer)
            return null;
        const { match, evidence } = criterionMatch(scoringCriterionOf(criterion), facts, workMode);
        return { match, reason: evidence };
    }
    const typed = [...facts.skills, ...facts.certifications];
    return typed.length === 0 ? null : { text: typed.join(', ') };
}
//# sourceMappingURL=match-score.js.map